import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';

import { getCookieConsent, setCookieConsent } from '../../utils/cookieUtils';

const CookieConsent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice using proper cookies
    const hasConsent = getCookieConsent();
    if (!hasConsent) {
      // Small delay to ensure page has loaded
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences - getCookieConsent already returns parsed object
      setPreferences(hasConsent);
      updateGTMConsent(hasConsent);
    }
  }, []);

  const updateGTMConsent = prefs => {
    // Update Google Tag Manager consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        functionality_storage: prefs.functional ? 'granted' : 'denied'
      });
    }

    // Alternative for dataLayer if gtag is not available
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'consent_update',
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        functionality_storage: prefs.functional ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    setCookieConsent(allAccepted);
    updateGTMConsent(allAccepted);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(onlyNecessary);
    setCookieConsent(onlyNecessary);
    updateGTMConsent(onlyNecessary);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    setCookieConsent(preferences);
    updateGTMConsent(preferences);
    setShowPreferences(false);
    setShowBanner(false);
  };

  const handlePreferenceChange = category => event => {
    if (category === 'necessary') return; // Cannot be disabled

    setPreferences(prev => ({
      ...prev,
      [category]: event.target.checked
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.25)',
          p: {
            xs: '12px 16px 10px 16px',
            md: '12px 0px 10px 0px'
          },
          px: { md: 3 },
          zIndex: 9999
        }}
      >
        <Box
          sx={{
            maxWidth: '1600px',
            mx: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 2, md: 2 }
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant={isMobile ? 'body2' : 'body1'}
              sx={{
                mb: 1,
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.9)'
              }}
            >
              We use cookies to enhance your experience
            </Typography>
            <Typography
              variant={isMobile ? 'caption' : 'body2'}
              sx={{
                lineHeight: 1.4,
                color: 'rgba(255, 255, 255, 0.7)'
              }}
            >
              We use essential cookies to make our website work and analytics
              cookies to understand how you interact with our website. By
              clicking "Accept All", you consent to the use of all cookies. You
              can customize your preferences or learn more in our{' '}
              <Box
                component='a'
                href='/legal/cookie-policy'
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  textDecoration: 'underline',
                  '&:hover': {
                    textDecoration: 'none',
                    color: 'rgba(255, 255, 255, 1)'
                  }
                }}
              >
                Cookie Policy
              </Box>
              .
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 2 },
              alignItems: 'stretch',
              minWidth: { xs: 'auto', md: '400px' }
            }}
          >
            <Button
              variant='outlined'
              onClick={() => setShowPreferences(true)}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                whiteSpace: 'nowrap',
                color: 'rgba(255, 255, 255, 0.7)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Customize
            </Button>
            <Button
              variant='outlined'
              onClick={handleRejectAll}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                whiteSpace: 'nowrap',
                color: 'rgba(255, 255, 255, 0.7)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Reject All
            </Button>
            <Button
              variant='contained'
              onClick={handleAcceptAll}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                whiteSpace: 'nowrap',
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  color: 'rgba(255, 255, 255, 1)'
                }
              }}
            >
              Accept All
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Cookie Preferences Dialog */}
      <Dialog
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            maxHeight: '90vh'
          }
        }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
            Cookie Preferences
          </Typography>

          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mb: 3, lineHeight: 1.5 }}
          >
            We use different types of cookies to optimize your experience on our
            website. You can choose which categories you'd like to allow. Please
            note that blocking some types of cookies may impact your experience
            on our website and the services we are able to offer.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Necessary Cookies */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.necessary}
                    disabled
                    color='primary'
                  />
                }
                label={
                  <Box>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      Necessary Cookies
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      These cookies are essential for the website to function
                      properly. They cannot be disabled.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>

            <Divider />

            {/* Analytics Cookies */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.analytics}
                    onChange={handlePreferenceChange('analytics')}
                    color='primary'
                  />
                }
                label={
                  <Box>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      Analytics Cookies
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      These cookies help us understand how visitors interact
                      with our website by collecting and reporting information
                      anonymously.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>

            <Divider />

            {/* Marketing Cookies */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.marketing}
                    onChange={handlePreferenceChange('marketing')}
                    color='primary'
                  />
                }
                label={
                  <Box>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      Marketing Cookies
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      These cookies are used to track visitors across websites
                      to display relevant and engaging advertisements.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>

            <Divider />

            {/* Functional Cookies */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.functional}
                    onChange={handlePreferenceChange('functional')}
                    color='primary'
                  />
                }
                label={
                  <Box>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      Functional Cookies
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      These cookies enable enhanced functionality and
                      personalization such as videos and live chats.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0 }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setShowPreferences(false)} color='inherit'>
            Cancel
          </Button>
          <Button onClick={handleSavePreferences} variant='contained'>
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
